import { t } from "@lingui/macro";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { AlertColor } from "@mui/material/Alert";
import axios from "axios";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Alerts from "@/client/components/alerts";
import ChipLabel from "@/client/components/custom/custom-chip";
import { useUser } from "@/client/services/user";

const ViewSelfAssessment = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const studentId = user?.id;

  const [assessment, setAssessment] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  useEffect(() => {
  if (!user?.id) return; // Wait until user is loaded
  fetchAssessment();
}, [id, user?.id]);


  const fetchAssessment = async () => {
    try {
      const res = await axios.get(
        `/api/integration/external-service/self-assessment/${id}`,
        {
          params: { studentId: user?.id }
        }
      );
      setAssessment(res.data.result);
    } catch (error) {
      console.error("Error fetching assessment:", error);
      setAlert({
        open: true,
        message: t`Failed to fetch assessment details`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAlert = () => {
    setAlert({ open: false, message: "", severity: "info" });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "--";
    try {
      return format(new Date(dateString), "MMM dd, yyyy hh:mm a");
    } catch (error) {
      return "--";
    }
  };

  const handleStatusChip = (status: string) => {
    switch (status.toLowerCase()) {
      case "draft":
        return (
          <ChipLabel
            label={t`Draft`}
            sx={{
              color: "grey.main",
              backgroundColor: "grey.light",
              fontSize: "14px",
              height: "32px",
            }}
          />
        );
      case "published":
        return (
          <ChipLabel
            label={t`Published`}
            sx={{
              color: "blue.main",
              backgroundColor: "blue.light",
              fontSize: "14px",
              height: "32px",
            }}
          />
        );
      case "completed":
        return (
          <ChipLabel
            label={t`Completed`}
            sx={{
              color: "green.main",
              backgroundColor: "green.light",
              fontSize: "14px",
              height: "32px",
            }}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography>{t`Loading...`}</Typography>
      </Container>
    );
  }

  if (!assessment) {
    return (
      <Container>
        <Typography>{t`Assessment not found`}</Typography>
      </Container>
    );
  }

  return (
    <Box>
      <Alerts
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />

      <Container maxWidth="lg">
        <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/self-assessments")}
            sx={{ textTransform: "none" }}
          >
            {t`Back to Self-Assessments`}
          </Button>

          <Stack direction="row" spacing={2}>
            {assessment.status === "draft" && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/self-assessments/edit/${id}`)}
                sx={{ textTransform: "none" }}
              >
                {t`Edit`}
              </Button>
            )}
            {assessment.status === "published" && !assessment.completedAt && (
              <Button
                variant="contained"
                color="success"
                startIcon={<PlayArrowIcon />}
                onClick={() => navigate(`/self-assessments/take/${id}`)}
                sx={{ textTransform: "none" }}
              >
                {t`Take Assessment`}
              </Button>
            )}
          </Stack>
        </Box>

        <Card>
          <CardContent sx={{ p: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                  {assessment.name}
                </Typography>
                {handleStatusChip(assessment.status)}
              </Stack>
              {assessment.description && (
                <Typography variant="body1" color="text.secondary">
                  {assessment.description}
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Details Grid */}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t`Category`}
                  </Typography>
                  <Typography variant="h6">
                    {assessment.category || "--"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t`Time Limit`}
                  </Typography>
                  <Typography variant="h6">
                    {assessment.time} {t`minutes`}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t`Questions`}
                  </Typography>
                  <Typography variant="h6">
                    {assessment.questionCount || 0}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t`Total Score`}
                  </Typography>
                  <Typography variant="h6">
                    {assessment.totalQuestionScore || 0}
                  </Typography>
                </Box>
              </Grid>

              {assessment.completedAt && (
                <>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t`Your Score`}
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {assessment.score}/{assessment.totalScore}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t`Completed At`}
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(assessment.completedAt)}
                      </Typography>
                    </Box>
                  </Grid>
                </>
              )}

              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t`Created At`}
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(assessment.createdAt)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Questions List */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                {t`Questions`} ({assessment.questions?.length || 0})
              </Typography>

              {assessment.questions?.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                  {t`No questions added yet.`}
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {assessment.questions?.map((question: any, index: number) => (
                    <Card key={question._id} variant="outlined">
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">
                          {t`Question`} {index + 1}
                        </Typography>
                        <Typography variant="body1" sx={{ my: 1 }}>
                          {question.question?.text || ""}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Chip label={question.type} size="small" />
                          {question.level && (
                            <Chip label={question.level} size="small" color="primary" />
                          )}
                          <Chip label={`${question.point} pts`} size="small" color="success" />
                          {question.technologies?.map((tech: string) => (
                            <Chip key={tech} label={tech} size="small" variant="outlined" />
                          ))}
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ViewSelfAssessment;